/**
 * ILNS - Evey Events / Horizon cart-drawer compatibility bridge.
 * Added 6 Sep 2026 as a P0 checkout hotfix. Theme-only, no app changes.
 *
 * THE PROBLEM
 * -----------
 * Horizon opens the cart drawer as a native <dialog> via showModal(), which
 * promotes it into the browser's TOP LAYER. Evey's attendee-information modal
 * (#evey-attendee-info-modal) is an ordinary position:fixed <div>. Nothing in
 * the normal stacking context can paint above a top-layer dialog no matter how
 * large its z-index - Evey already uses 2147483638 and still loses - and the
 * dialog's ::backdrop swallows pointer events across the entire viewport.
 *
 * So on mobile, tapping "Check out" in the cart drawer looked like nothing
 * happened. Evey's form HAD opened, underneath the cart. Closing the cart by
 * hand revealed it instantly.
 *
 * This is not a z-index bug and cannot be fixed with z-index. The dialog has to
 * leave the top layer.
 *
 * THE FIX
 * -------
 * Close the cart drawer at the moment the customer commits to checkout, then
 * let the original event continue untouched so Evey opens and drives its own
 * form exactly as it does on the /cart page (where there is no dialog and the
 * flow already works).
 *
 * Everything is delegated from `document` in the CAPTURE phase, because the
 * cart drawer is re-rendered through the Section Rendering API on every add,
 * remove and quantity change, and Evey injects its overlay asynchronously.
 * Listeners bound directly to drawer nodes would be thrown away on the next
 * morph.
 */
(function () {
  'use strict';

  // Guard against double-execution (the script tag can be re-evaluated when
  // assets/morph.js walks over and re-creates <script> nodes during a morph).
  if (window.__ilnsEveyCartBridge) return;
  window.__ilnsEveyCartBridge = true;

  var CART_DRAWER_ID = 'cart-drawer';
  var EVEY_MODAL_ID = 'evey-attendee-info-modal';
  var EVEY_HIDDEN_CLASS = 'visually-hidden';

  function cartDrawer() {
    return document.getElementById(CART_DRAWER_ID);
  }

  function cartDrawerPanel() {
    var drawer = cartDrawer();
    return drawer ? drawer.querySelector('dialog') : null;
  }

  /**
   * Close ONLY the cart drawer, and leave the top layer synchronously.
   *
   * ThemeDrawer#close() is async: it awaits the CSS close animation and only
   * then calls panel.close(), so the dialog would linger in the top layer for
   * the length of that animation and keep covering Evey's form. We therefore
   * call panel.close() ourselves first, which exits the top layer immediately,
   * and then hand off to the component so it still unlocks body scroll, drops
   * its keydown/backdrop listeners, releases focus trapping and clears its own
   * open state. Because panel.open is already false by that point, ThemeDrawer
   * skips its animation branch entirely and the whole close is synchronous.
   *
   * Returns true only if a genuinely open cart drawer was closed.
   */
  function closeCartDrawer() {
    var drawer = cartDrawer();
    var panel = cartDrawerPanel();
    if (!drawer || !panel || !panel.open) return false;

    try {
      panel.close();
    } catch (error) {
      /* older engines: fall through, the component close still runs */
    }

    if (typeof drawer.close === 'function') {
      try {
        drawer.close();
      } catch (error) {
        drawer.removeAttribute('open');
      }
    } else {
      drawer.removeAttribute('open');
    }

    return true;
  }

  /**
   * Is Evey actually going to intervene on this click?
   *
   * Evey signals that a cart needs attendee details by injecting a transparent
   * .evey-overlay-checkout on top of the checkout button. If that overlay is
   * absent, this is an ordinary merchandise cart heading straight to Shopify
   * checkout and we must not touch the drawer at all - merchandise-only
   * checkout has to behave exactly as it did before this file existed.
   */
  function eveyGuardsCheckout() {
    return !!document.querySelector('#' + CART_DRAWER_ID + ' .evey-overlay-checkout');
  }

  // ---------------------------------------------------------------------
  // Primary handler: the customer taps checkout inside the open cart drawer.
  // ---------------------------------------------------------------------
  function onCheckoutIntent(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    // Only ever act on things inside the cart drawer. Search dialogs, gallery
    // dialogs, the country selector and any app drawer are none of our
    // business.
    if (!target.closest('#' + CART_DRAWER_ID)) return;

    var overlay = target.closest('.evey-overlay-checkout');
    var checkoutButton = target.closest('button[name="checkout"], #checkout');
    if (!overlay && !checkoutButton) return;

    // A click on the native checkout button only counts when Evey has claimed
    // this cart. A click on Evey's own overlay is self-evidently Evey's.
    if (!overlay && !eveyGuardsCheckout()) return;

    closeCartDrawer();

    // Deliberately NO preventDefault and NO stopPropagation: Evey's own
    // handler must still receive this exact click so it can open and process
    // the attendee form. Testing confirmed the handoff works with the event
    // left completely intact.
  }

  document.addEventListener('click', onCheckoutIntent, true);

  // ---------------------------------------------------------------------
  // Fallback: Evey's modal becomes visible while the drawer is still open.
  //
  // Covers any route we did not anticipate - a slow Evey script that binds
  // after our click ran, or Evey opening its form from somewhere other than
  // the overlay. This is not theoretical: after a quantity change the drawer
  // re-renders and Evey does not re-inject its overlay, yet it still
  // intercepts the checkout button, so this observer is what closes the
  // drawer on that path. Narrow and cheap on purpose: we watch the class
  // attribute of one known element, never the whole page.
  // ---------------------------------------------------------------------
  function eveyModalIsVisible(modal) {
    if (!modal) return false;
    if (modal.classList.contains(EVEY_HIDDEN_CLASS)) return false;
    return modal.offsetWidth > 0 || modal.offsetHeight > 0;
  }

  /**
   * Put focus into Evey's form once it has finished rendering.
   *
   * Two reasons this is needed. Evey paints a loading spinner first and fills
   * the fields in afterwards, and ThemeDrawer#close() restores focus to
   * whatever opened the drawer (the header cart button), so without this the
   * keyboard and screen-reader position would be left behind the form the
   * customer is looking at.
   *
   * Bounded to a few seconds of polling, then it gives up quietly.
   */
  function focusEveyForm(modal) {
    var attempts = 0;
    var MAX_ATTEMPTS = 30; // ~4.5s at 150ms

    (function attempt() {
      if (attempts++ > MAX_ATTEMPTS) return;
      if (!eveyModalIsVisible(modal)) return;

      // If Evey already moved focus into its own form, leave it alone.
      if (modal.contains(document.activeElement)) return;

      var field = modal.querySelector(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
      );

      if (field) {
        try {
          field.focus({ preventScroll: false });
        } catch (error) {
          field.focus();
        }
        return;
      }

      window.setTimeout(attempt, 150);
    })();
  }

  function onEveyModalMutation(modal) {
    if (!eveyModalIsVisible(modal)) return;
    closeCartDrawer();
    focusEveyForm(modal);
  }

  function observeEveyModal(modal) {
    if (!modal || modal.__ilnsObserved) return;
    modal.__ilnsObserved = true;

    new MutationObserver(function () {
      onEveyModalMutation(modal);
    }).observe(modal, { attributes: true, attributeFilter: ['class', 'style'] });

    // Evey may already have opened it before we got here.
    onEveyModalMutation(modal);
  }

  function watchForEveyModal() {
    var modal = document.getElementById(EVEY_MODAL_ID);
    if (modal) {
      observeEveyModal(modal);
      return;
    }

    // Evey had not injected its modal yet. Watch only direct children of
    // <body> (subtree:false) until it appears, then stop. Evey appends its
    // modal wrapper at body level, so this is enough and stays cheap.
    var bodyObserver = new MutationObserver(function () {
      var found = document.getElementById(EVEY_MODAL_ID);
      if (!found) return;
      bodyObserver.disconnect();
      observeEveyModal(found);
    });

    bodyObserver.observe(document.body, { childList: true, subtree: false });

    // Never leave an observer running forever.
    window.setTimeout(function () {
      bodyObserver.disconnect();
      var late = document.getElementById(EVEY_MODAL_ID);
      if (late) observeEveyModal(late);
    }, 20000);
  }

  // ---------------------------------------------------------------------
  // Ticket add-to-cart confirmation.
  //
  // Opening the drawer is no longer done here. Horizon opens it natively when
  // the theme setting auto_open_cart_drawer is on, which covers every product
  // and fires sooner than this file could. All that remains is the
  // screen-reader confirmation, which is ticket specific.
  // ---------------------------------------------------------------------
  var pendingTicketAdd = null;

  function readCart() {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return fetch(root + 'cart.js?_=' + Date.now(), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Horizon's cart event carries a `promise` that resolves once the cart
   * operation has actually finished, together with the lines that changed and
   * a didError flag.
   *
   * This replaces an earlier approach that polled /cart.js until it caught up,
   * because the event fires before /cart.js serves the updated cart. That poll
   * cost up to eight requests on every single cart change. Waiting on the
   * promise is both cheaper and exact.
   */
  function whenCartSettled(event) {
    if (event && event.promise && typeof event.promise.then === 'function') {
      return event.promise.then(
        function (result) {
          return (result && result.detail) || null;
        },
        function () {
          return null;
        }
      );
    }
    return Promise.resolve(null);
  }

  // ---------------------------------------------------------------------
  // Express-wallet gate.
  //
  // Evey guards the ordinary checkout button, but it has no way to guard the
  // Shop Pay and PayPal buttons in .cart__ctas: those are rendered by Shopify
  // inside its own iframe, so Evey cannot overlay or intercept them. A ticket
  // bought through one of them would reach checkout with no attendee details
  // collected at all.
  //
  // While the cart holds a ticket we therefore hide the express buttons and
  // leave only the guarded "Check out" path. Merchandise-only carts keep every
  // express option exactly as before.
  //
  // Doing this in JS rather than Liquid is complete rather than partial,
  // because the wallet buttons are themselves injected by Shopify script - with
  // JS off there is nothing rendered to bypass.
  // ---------------------------------------------------------------------
  var GATE_STYLE_ID = 'ilns-ticket-checkout-gate';

  function ensureGateStyle() {
    if (document.getElementById(GATE_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = GATE_STYLE_ID;
    style.textContent =
      '[data-ilns-ticket-cart="true"] .cart__ctas .additional-checkout-buttons{display:none !important;}';
    document.head.appendChild(style);
  }

  /**
   * The gate can be lifted from snippets/cart-drawer.liquid by setting
   * ilns_allow_ticket_express to true, which renders
   * data-ilns-allow-ticket-express on the drawer.
   *
   * Only do that once Evey is collecting attendee details AFTER purchase.
   * Wallet buttons live in a Shopify iframe that Evey cannot overlay, so with
   * the gate lifted a Shop Pay ticket order reaches checkout with no attendee
   * names attached, and door staff have nothing to check against.
   */
  function ticketExpressAllowed() {
    var drawer = cartDrawer();
    return !!(drawer && drawer.hasAttribute('data-ilns-allow-ticket-express'));
  }

  function applyTicketCheckoutGate(cart) {
    var hasTicket =
      !!cart &&
      !!cart.items &&
      cart.items.some(function (item) {
        return String(item.product_type).toLowerCase() === 'event';
      });

    ensureGateStyle();

    if (hasTicket && !ticketExpressAllowed()) {
      document.documentElement.setAttribute('data-ilns-ticket-cart', 'true');
    } else {
      document.documentElement.removeAttribute('data-ilns-ticket-cart');
    }
  }

  function announce(message) {
    var region = document.getElementById('ilns-cart-status');

    if (!region) {
      region = document.createElement('p');
      region.id = 'ilns-cart-status';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      // Visually hidden, still announced.
      region.style.cssText =
        'position:absolute;width:1px;height:1px;margin:-1px;padding:0;' +
        'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0;';
      document.body.appendChild(region);
    }

    // Clear first so an identical repeat add is still announced.
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  // Record which variant is being added, so that when the cart reports success
  // we can tell whether it was a ticket without guessing from cart contents.
  function rememberAddIntent(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var form = target.closest('form[action*="/cart/add"]');
    if (!form) return;

    var trigger = target.closest('button[name="add"], [type="submit"]');
    if (event.type === 'click' && !trigger) return;

    var idField = form.querySelector('[name="id"]');
    var variantId = idField && idField.value ? String(idField.value).trim() : '';
    if (!variantId) return; // the theme's purchase guard blanks this until options are chosen

    pendingTicketAdd = { variantId: variantId, at: Date.now() };
  }

  document.addEventListener('click', rememberAddIntent, true);
  document.addEventListener('submit', rememberAddIntent, true);

  // ---------------------------------------------------------------------
  // Keep the checkout button on screen after an auto-opened drawer.
  //
  // Horizon pins the cart summary to the bottom of the drawer by setting
  // cart-summary-sticky on the dialog, but only when the summary takes up less
  // than half the drawer. It measures that ratio when the drawer opens and once
  // on the next animation frame. With auto-open enabled both measurements can
  // land before the cart section has morphed in, so the drawer opens with
  // sticky switched off and the Check out button pushed below the fold - it sat
  // at 984px on a 844px tall screen here, while a manual reopen of the exact
  // same cart measured the same 0.421 ratio and correctly pinned it at 650px.
  //
  // We simply re-apply Horizon's own rule a few times once the cart has
  // settled. Same threshold, same attribute, no patching of theme internals.
  // ---------------------------------------------------------------------
  var SUMMARY_STICKY_THRESHOLD = 0.5;

  function refreshCartSummarySticky() {
    var drawer = cartDrawer();
    if (!drawer) return;

    var dialog = drawer.querySelector('dialog');
    if (!dialog || !dialog.open) return;

    var content = dialog.querySelector('.cart-drawer__content');
    var summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    var drawerHeight = dialog.getBoundingClientRect().height;
    if (!drawerHeight) return;

    var ratio = summary.getBoundingClientRect().height / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > SUMMARY_STICKY_THRESHOLD ? 'false' : 'true');
  }

  // The morph can land a frame or two after the cart promise resolves, so
  // re-measure a few times rather than guessing one moment. Cheap, bounded,
  // and it settles on the same answer Horizon would have reached.
  function scheduleStickyRefresh() {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(refreshCartSummarySticky);
    });
    window.setTimeout(refreshCartSummarySticky, 200);
    window.setTimeout(refreshCartSummarySticky, 500);
  }

  // ---------------------------------------------------------------------
  // Optimistic drawer open: make Add to cart feel instant.
  //
  // Horizon's auto-open waits for the cart request to come back. Measured on
  // this store that is ~1.8s on the first add, of which ~1.2s is Shopify's own
  // /cart/add.js and the rest is the drawer section render. Neither is
  // something the theme can speed up.
  //
  // So we stop waiting: the drawer opens on the click itself, in a pending
  // state, and the real cart content morphs in when it arrives. Subsequent
  // adds already opened in ~53ms, so this is mainly about the first one.
  //
  // Every existing guard is respected before we open, so a blocked add never
  // pops the drawer: native form validity, the disabled property, and the
  // theme's own aria-disabled purchase guard that keeps Add to cart inert
  // until all variant options are chosen.
  // ---------------------------------------------------------------------
  var PENDING_ATTR = 'data-ilns-cart-pending';
  var pendingSawCartEvent = false;

  function pendingPanel() {
    var panel = cartDrawerPanel();
    if (!panel) return null;

    var existing = panel.querySelector('.ilns-cart-pending');
    if (existing) return existing;

    var node = document.createElement('div');
    node.className = 'ilns-cart-pending';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.hidden = true;
    node.innerHTML =
      '<span class="ilns-cart-pending__spinner" aria-hidden="true"></span>' +
      '<span class="ilns-cart-pending__label">Adding to your cart</span>';
    panel.appendChild(node);
    return node;
  }

  function setCartPending(on) {
    var drawer = cartDrawer();
    if (!drawer) return;

    var node = pendingPanel();

    if (on) {
      drawer.setAttribute(PENDING_ATTR, '');
      // Only show the spinner when there is nothing else to look at. With a
      // cart that already has lines, the drawer opens straight onto them and
      // the new line simply appears.
      var isEmpty = !!drawer.querySelector('.cart-drawer--empty');
      if (node) node.hidden = !isEmpty;
    } else {
      drawer.removeAttribute(PENDING_ATTR);
      if (node) node.hidden = true;
    }
  }

  function onAddToCartClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest('button[name="add"], add-to-cart-component button');
    if (!button) return;

    var form = button.closest('form[action*="/cart/add"]');
    if (!form) return;

    // Mirror every guard Horizon and this theme already apply.
    if (button.disabled) return;
    if (button.getAttribute('aria-disabled') === 'true') return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

    var idField = form.querySelector('[name="id"]');
    if (!idField || !String(idField.value).trim()) return;

    // Quick add runs inside its own modal and Horizon deliberately defers the
    // drawer until that modal closes. Leave that flow alone.
    if (button.closest('dialog:modal') && !button.closest('#' + CART_DRAWER_ID)) return;

    var drawer = cartDrawer();
    if (!drawer || drawer.hasAttribute('open')) return;
    if (typeof drawer.open !== 'function') return;

    pendingSawCartEvent = false;
    setCartPending(true);
    drawer.open();

    // If no cart event arrives, the add never actually happened (a quantity
    // rule, an app veto, a dropped request). Undo the optimistic open rather
    // than leaving an empty drawer sitting there.
    window.setTimeout(function () {
      if (pendingSawCartEvent) return;
      setCartPending(false);
      var stillEmpty = drawer.querySelector('.cart-drawer--empty');
      if (stillEmpty && drawer.hasAttribute('open')) closeCartDrawer();
    }, 2000);
  }

  document.addEventListener('click', onAddToCartClick, true);

  document.addEventListener('shopify:cart:lines-update', function (event) {
    var intent = pendingTicketAdd;
    pendingTicketAdd = null;

    pendingSawCartEvent = true;

    whenCartSettled(event).then(function (detail) {
      setCartPending(false);

      // A failed add must never announce success or move the express gate.
      if (detail && detail.didError) {
        // Nothing was added. If the drawer only opened optimistically for this
        // add, do not leave it open on an empty cart.
        if (cartDrawer() && cartDrawer().querySelector('.cart-drawer--empty')) closeCartDrawer();
        return;
      }

      // detail.items is the lines that just changed, so the ticket is
      // identified without another round trip and without inferring from
      // totals. A removal or quantity edit carries no pending add, so it
      // cannot announce.
      if (intent && detail && detail.items && detail.items.length) {
        var addedTicket = detail.items.some(function (item) {
          return (
            String(item.variant_id) === intent.variantId &&
            String(item.product_type).toLowerCase() === 'event'
          );
        });
        if (addedTicket) announce('Ticket added to cart');
      }

      // Re-pin the checkout button once the drawer has its final content.
      scheduleStickyRefresh();

      // The gate needs whole-cart state, which the changed lines alone do not
      // give us. One read is enough now that the promise guarantees the cart
      // is current.
      return readCart().then(function (cart) {
        if (cart) applyTicketCheckoutGate(cart);
      });
    });
  });

  // Set the express-wallet gate for the cart this page was loaded with.
  readCart().then(function (cart) {
    if (cart) applyTicketCheckoutGate(cart);
  });

  // ---------------------------------------------------------------------
  // Ticket pages: an always-visible buy bar.
  //
  // On the event template the tier picker sits ~954px down and Add to cart at
  // ~1279px, so on a phone a buyer arriving from Instagram scrolls about one
  // and a half screens before they can do anything. Horizon already renders a
  // sticky buy bar carrying the image, title and price, but it stays hidden
  // until a tier is chosen (the theme's deliberate no-preselect guard) and
  // until the buy buttons have scrolled through the viewport.
  //
  // The guard is kept exactly as it is - nothing is preselected and nothing
  // can be added by accident. The bar is simply shown from page load, with a
  // Choose ticket action that jumps to the picker. Once a tier is chosen it
  // becomes the ordinary Add to cart bar Horizon already built.
  //
  // Before a tier is chosen the thumbnail and the full product title are
  // dropped from the bar. Measured at 390px they squeezed the title block to
  // 48px and pushed the bar into horizontal overflow. On a product page the
  // customer already knows what they are looking at, so the bar carries the
  // price and the action only.
  // ---------------------------------------------------------------------
  var EVENT_PAGE_ATTR = 'data-ilns-event-page';
  var TICKET_STYLE_ID = 'ilns-ticket-checkout-styles';

  function isEventProductPage() {
    var main = document.getElementById('MainContent');
    var template = main && main.getAttribute('data-template');
    if (template && template.indexOf('product.event') === 0) return true;

    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      var product = meta && meta.product;
      return !!(product && String(product.type).toLowerCase() === 'event');
    } catch (error) {
      return false;
    }
  }

  function ensureTicketStyles() {
    if (document.getElementById(TICKET_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = TICKET_STYLE_ID;
    style.textContent = [
      '[' + EVENT_PAGE_ATTR + '] .sticky-add-to-cart__bar{opacity:1 !important;transform:translate(-50%) translateY(0) !important;}',
      '[' + EVENT_PAGE_ATTR + '] sticky-add-to-cart:not([data-variant-available="true"]) .sticky-add-to-cart__bar{display:flex !important;}',
      '[' + EVENT_PAGE_ATTR + '] sticky-add-to-cart:not([data-variant-available="true"]) .sticky-add-to-cart__button{display:none !important;}',
      '[' + EVENT_PAGE_ATTR + '] sticky-add-to-cart:not([data-variant-available="true"]) .sticky-add-to-cart__image,',
      '[' + EVENT_PAGE_ATTR + '] sticky-add-to-cart:not([data-variant-available="true"]) .sticky-add-to-cart__info{display:none !important;}',
      '[' + EVENT_PAGE_ATTR + '] sticky-add-to-cart:not([data-variant-available="true"]) .ilns-choose-ticket{flex:1 1 auto;}',
      '[' + EVENT_PAGE_ATTR + '] sticky-add-to-cart[data-variant-available="true"] .ilns-choose-ticket{display:none !important;}',
      '.ilns-choose-ticket{height:var(--height-buy-buttons,48px);padding-inline:18px;border:0;border-radius:0;',
      'background:var(--color-foreground,#000);color:var(--color-background,#fff);font-family:var(--font-subheading--family,inherit);',
      'font-size:0.75rem;letter-spacing:0.1em;text-transform:uppercase;white-space:nowrap;cursor:pointer;}',
      '.ilns-choose-ticket:focus-visible{outline:2px solid currentColor;outline-offset:2px;}',
      '[' + EVENT_PAGE_ATTR + '] .variant-option__button-label:has(input[aria-disabled="true"]){',
      'min-height:32px;padding-block:2px;font-size:0.6875rem;opacity:0.45;}',
      '.ilns-tier-flash{outline:2px solid currentColor;outline-offset:2px;}'
    ].join('');
    document.head.appendChild(style);
  }

  function ensureChooseTicketButton() {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar || bar.querySelector('.ilns-choose-ticket')) return;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'ilns-choose-ticket';
    button.textContent = 'Choose ticket';

    button.addEventListener('click', function () {
      var picker = document.querySelector('.variant-picker');
      if (!picker) return;

      try {
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        picker.scrollIntoView();
      }

      // Focus the picker container, never a radio: focusing a radio can select
      // it in some browsers, which would defeat the no-preselect guard.
      picker.setAttribute('tabindex', '-1');
      try {
        picker.focus({ preventScroll: true });
      } catch (error) {
        /* focus is a convenience here, not a requirement */
      }

      var firstAvailable = picker.querySelector('input:not([aria-disabled="true"])');
      var label = firstAvailable && (firstAvailable.closest('label') || firstAvailable.parentElement);
      if (label) {
        label.classList.add('ilns-tier-flash');
        window.setTimeout(function () {
          label.classList.remove('ilns-tier-flash');
        }, 1600);
      }
    });

    bar.appendChild(button);
  }

  function setUpTicketPage() {
    if (!isEventProductPage()) return;
    document.documentElement.setAttribute(EVENT_PAGE_ATTR, '');
    ensureTicketStyles();
    ensureChooseTicketButton();
  }

  // ---------------------------------------------------------------------
  function boot() {
    watchForEveyModal();
    setUpTicketPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
